import { MetaQuery, useMany, useOne } from '@refinedev/core';
import { SelectProps } from 'antd/lib';
import { useMemo, useRef } from 'react';

interface UseSelectPropsWithInitialParams {
  selectProps: SelectProps;
  resource: string;
  initialValue?: string | number | null | (string | number)[]; // Puede ser un array en caso de select multiple
  labelKey: string | ((item: any) => string);
  valueKey: string;
  disabled?: boolean;
  getOption?: (data: any) => any;
  meta?: MetaQuery;
}

/**
 * Hook que asegura que un `Select` cuente con las opciones asociadas a los valores iniciales.
 * Se apoya en `useOne`/`useMany` de Refine para traer los registros que no están en las opciones actuales
 * (por ejemplo, porque fueron paginados o vienen seteados desde la URL) y los inserta sólo una vez.
 *
 * Está pensado para envolver `selectProps` provenientes de distintos orígenes (scroll infinito, catálogos precargados, etc.)
 * y se puede combinar con `useSelectScrollSearch` componiendo ambos hooks.
 *
 * @param selectProps Propiedades del select que se desean extender.
 * @param resource Recurso de Refine desde el cual traer los datos iniciales.
 * @param initialValue Valor o lista de valores que deberían mostrarse como opción inicial.
 * @param labelKey Clave o función para obtener la etiqueta de la opción.
 * @param valueKey Clave que define el identificador de la opción.
 * @param disabled Evita la consulta si el select está deshabilitado.
 * @param getOption Función opcional para construir manualmente la opción.
 * @param meta Metadatos adicionales para las consultas de Refine.
 * @returns `SelectProps` con las opciones actualizadas.
 */
export function useSelectPropsWithInitial({
  selectProps,
  resource,
  initialValue,
  labelKey,
  valueKey,
  disabled = false,
  getOption,
  meta,
}: UseSelectPropsWithInitialParams) {
  // Bloquea la consulta una vez se seteo el valor inicial por primera vez, tambien permite que no se añada nuevamente al resetear las opciones
  const lockInitial = useRef(false);
  // Solo busca si initialValue no está en options
  const shouldFetchInitial = useMemo(
    () =>
      !!initialValue &&
      !selectProps.options?.some(opt => opt.value === initialValue) &&
      !lockInitial.current,
    [initialValue, selectProps.options],
  );
  const singleInitialValue = useMemo(
    () => (!Array.isArray(initialValue) ? initialValue : undefined),
    [initialValue],
  );
  const multiInitialValue = useMemo(
    () => (Array.isArray(initialValue) ? initialValue.filter(v => !!v) : undefined),
    [initialValue],
  );
  // Hook Refine para traer ese único dato
  const { result: singleInitialResult } = useOne({
    resource,
    id: singleInitialValue!,
    queryOptions: {
      enabled: shouldFetchInitial && !selectProps.loading && !disabled && !!singleInitialValue,
      staleTime: 0, // siempre lo considera stale, por lo que lo vuelve a pedir
      retry: false, // no reintenta en caso de error
    },
    meta: meta ? meta : undefined,
  });
  // Hook Refine para traer datos multiples
  const {
    result: { data: multiInitialResult },
  } = useMany({
    resource,
    ids: multiInitialValue || [],
    queryOptions: {
      enabled: shouldFetchInitial && !selectProps.loading && !disabled && !!multiInitialValue,
      staleTime: 0, // siempre lo considera stale, por lo que lo vuelve a pedir
      retry: false, // no reintenta en caso de error
    },
    meta: meta ? meta : undefined,
  });
  // Option inicial solo si es necesario
  const initialOptions = useMemo(() => {
    if (
      shouldFetchInitial &&
      (singleInitialResult || multiInitialResult.length > 0) &&
      initialValue !== null
    ) {
      lockInitial.current = true; // Evita que se vuelva a buscar
      const result = singleInitialResult ? [singleInitialResult] : multiInitialResult;
      const options = result.map(initialResult =>
        getOption
          ? getOption(initialResult)
          : {
              label:
                typeof labelKey === 'function' ? labelKey(initialResult) : initialResult[labelKey],
              value: initialResult[valueKey],
              item: initialResult,
            },
      );

      return options;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleInitialResult, multiInitialResult]);
  // Inserta al inicio solo si hace falta
  const options = useMemo(() => {
    if (!initialOptions || initialOptions.length === 0) {
      return selectProps.options || [];
    }

    // Agrega todos los que no esten ya en options
    const newOptions = initialOptions.filter(
      opt => !selectProps.options?.some(o => o.value === opt.value),
    );

    if (newOptions.length > 0) {
      return [...newOptions, ...(selectProps.options || [])];
    }

    return selectProps.options || [];
  }, [selectProps.options, initialOptions]);

  return {
    ...selectProps,
    options,
  };
}
